import { createReducer, on } from '@ngrx/store';
import { DateRange } from 'src/app/types/date-range';
import {
  createErrorNetworkState,
  createLoadingNetworkState,
  createQuietNetworkState,
} from '../../modules/utils/helpers/network-entities';
import { LoadingState } from '../../modules/utils/types';
import { BloomingData } from '../../modules/report/types/blooming-data';
import { PlantReport } from '../../modules/report/types/plant-report';
import { ReportActions } from './report.actions';
import { TopListEntry } from 'src/app/modules/report/types/top-list-entry';
import { TopPlantEntry } from 'src/app/modules/report/types/top-plant-entry';
import { ResultActions } from './result.actions';
import { ReportSpot } from 'src/app/modules/report/types/report-spot';

function normalizeLivedataYear(year?: number) {
  return year && year > 0 ? year : new Date().getFullYear();
}

export interface ReportState {
  reportListLoading: LoadingState;
  reportListCacheFlag: boolean;
  result: {};   // NOTE: look into this / what is it
  reports: PlantReport[];
  recentBloomingData: BloomingData[];
  bloomingDataFilter: {
    plants: number[];
    dateRange: DateRange;
    year: number;
    locationId: number;
  };
  livedataByKey: { [key: string]: BloomingData[] };
  livedataLoadingByKey: { [key: string]: LoadingState };
  activeLivedataKey: string;

  toplistData: TopListEntry[];
  toplistDataCacheFlag: boolean;
  toplistDataLoading: LoadingState;

  topplantsData: TopPlantEntry[];
  topplantsDataCacheFlag: boolean;
  topplantsDataLoading: LoadingState;

  spots: ReportSpot[];
  spotsCacheFlag: boolean;
  spotsDataLoading: LoadingState;

  activeSpot: ReportSpot;
}

// export interface State extends fromRoot.State {
//   report: ReportState;
// }

const initialState: ReportState = {
  reportListCacheFlag: false,
  reportListLoading: createQuietNetworkState(),
  result: {},
  reports: [],
  recentBloomingData: [],
  livedataByKey: {},
  livedataLoadingByKey: {},
  activeLivedataKey: null,
  bloomingDataFilter: {
    plants: [],
    dateRange: {
      fromDate: new Date(new Date().getFullYear(), 0, 1),
      toDate: new Date(new Date().getFullYear(), 11, 31),
    },
    locationId: null,   // No location

    // Year that is shown by default in the reporting view data section
    // either a year (e.g. 2025), or 0 for "all years"
    // year: new Date().getFullYear() - 1, // Last year
    // year: 0 // No year (all years)
    year: new Date().getFullYear()
  },
  toplistData: [],
  toplistDataCacheFlag: false,
  toplistDataLoading: createQuietNetworkState(),

  topplantsData: [],
  topplantsDataCacheFlag: false,
  topplantsDataLoading: createQuietNetworkState(),

  spots: [],
  spotsCacheFlag: false,
  spotsDataLoading: createQuietNetworkState(),
  activeSpot: null
};

export const ReportReducer = createReducer(
  initialState,

  on(ReportActions.reportSubmitted, (state, { report }) => ({
    ...state,
    result: report,
    error: false,
  })),
  on(ReportActions.reportSubmitOk, (state, { data }) => ({
    ...state,
    error: true,
    result: data,
  })),
  on(ReportActions.reportSubmitError, (state) => ({
    ...state,
    error: false,
  })),

  // get reports
  on(ReportActions.fetchReports, (state) => ({
    ...state,
    error: false,
    reportListLoading: createLoadingNetworkState(),
    reportListCacheFlag: false,
  })),
  on(ReportActions.getReportsSucess, (state, { reports }) => ({
    ...state,
    reports,
    reportListLoading: createQuietNetworkState(),
    reportListCacheFlag: true,
  })),
  on(ReportActions.getReportsError, (state, action) => ({
    ...state,
    error: true,
    reportListLoading: createErrorNetworkState(action.error),
  })),

  // delete reports
  on(ReportActions.deleteReportRequest, (state, { id }) => ({
    ...state,
    result: id,
    error: false,
  })),
  on(ReportActions.deleteReportSucess, (state, { result }) => ({
    ...state,
    result,
    error: true,
  })),
  on(ReportActions.deleteReportError, (state) => ({
    ...state,
    error: false,
  })),

  on(ResultActions.filterBloomingDataByPlant, (state, { plant }) => {

    // Add (select) the plant - unless it is already selected (then remove/unselect)
    let plants = state.bloomingDataFilter.plants.find((id) => id === plant?.id)
      ? state.bloomingDataFilter.plants.filter((id) => id !== plant.id)
      : state.bloomingDataFilter.plants.concat(plant?.id);

    // If *all* plants are selected, reset to the empty list
    if (plants?.length === state.recentBloomingData?.length) {
      plants = [];
    }

    return {
      ...state,
      bloomingDataFilter: {
        ...state.bloomingDataFilter,
        plants
      }
    }
  }),
  on(ResultActions.clearPlantSelection, (state) => ({
    ...state,
    bloomingDataFilter: {
      ...state.bloomingDataFilter,
      plants: [],
    },
  })),
  on(ResultActions.setFilterDateRange, (state, { dateRange }) => ({
    ...state,
    bloomingDataFilter: {
      ...state.bloomingDataFilter,
      dateRange,
      year: dateRange.fromDate ? dateRange.fromDate.getFullYear() : state.bloomingDataFilter.year
    },
  })),

  on(ResultActions.setBaseFilter, (state, props) => {
    const year = normalizeLivedataYear(props.state?.year);
    return {
      ...state,
      bloomingDataFilter: {
        ...state.bloomingDataFilter,
        ...props.state,
        year,
        dateRange: { fromDate: new Date(year, 0, 1), toDate: new Date(year, 11, 31) },
        plants: []
      }
    };
  }),
  on(ResultActions.fetchRecentBloomingDataOk, (state, { items }) => ({
    ...state,
    recentBloomingData: items,
  })),
  on(ResultActions.fetchRecentBloomingDataFail, (state, action) => ({
    ...state,
    recentBloomingData: [],
  })),

  // Livedata results keyed by year
  on(ResultActions.fetchLivedata, (state) => {
    const key = getLivedataQueryKey(state.bloomingDataFilter.year);
    return {
      ...state,
      activeLivedataKey: key,
      livedataLoadingByKey: {
        ...state.livedataLoadingByKey,
        [key]: createLoadingNetworkState(),
      }
    };
  }),
  on(ResultActions.fetchLivedataOk, (state, { queryKey, items }) => ({
    ...state,
    activeLivedataKey: queryKey,
    livedataByKey: {
      ...state.livedataByKey,
      [queryKey]: items,
    },
    livedataLoadingByKey: {
      ...state.livedataLoadingByKey,
      [queryKey]: createQuietNetworkState(),
    }
  })),
  on(ResultActions.fetchLivedataFail, (state, { queryKey, error }) => ({
    ...state,
    activeLivedataKey: queryKey,
    livedataLoadingByKey: {
      ...state.livedataLoadingByKey,
      [queryKey]: createErrorNetworkState(error),
    }
  })),

  // Users Toplist
  on(ResultActions.fetchTopList, (state) => ({
    ...state,
    toplistData: [],
    toplistDataLoading: createLoadingNetworkState(),
    toplistDataCacheFlag: false,
  })),
  on(ResultActions.fetchTopListOk, (state, { items }) => ({
    ...state,
    toplistData: items,
    toplistDataLoading: createQuietNetworkState(),
    toplistDataCacheFlag: true,
  })),
  on(ResultActions.fetchTopListFail, (state, action) => ({
    ...state,
    toplistData: [],
    toplistDataLoading: createErrorNetworkState(action.error),
  })),

  // Plants Toplist
  on(ResultActions.fetchTopPlants, (state) => ({
    ...state,
    topplantsData: [],
    topplantsDataLoading: createLoadingNetworkState(),
    topplantsDataCacheFlag: false,
  })),
  on(ResultActions.fetchTopPlantsOk, (state, { items }) => ({
    ...state,
    topplantsData: items,
    topplantsDataLoading: createQuietNetworkState(),
    topplantsDataCacheFlag: true,
  })),
  on(ResultActions.fetchTopPlantsFail, (state, action) => ({
    ...state,
    topplantsData: [],
    topplantsDataLoading: createErrorNetworkState(action.error),
  })),
  on(ResultActions.resetMapPlantFilter, (state) => ({
    ...state,
    bloomingDataFilter: {
      ...state.bloomingDataFilter,
      plants: []
    }
  })),

  // Fetch Spots
  on(ReportActions.fetchSpotsSuccess, (state, { spots }) => ({
    ...state,
    spots
  })),
  on(ReportActions.selectSpot, (state, { spot }) => ({
    ...state,
    activeSpot: spot
  }))
);

function getLivedataQueryKey(year: number) {
  if (!year) {
    return null;
  }
  return `${normalizeLivedataYear(year)}`;
}
